import React from "react";
import { MdChevronRight } from "react-icons/md";
import businessIllustration from "../../images/bar-chart.svg";
import personalIllustration from "../../images/credit-card.svg";
import zForHerIllustration from "../../images/crosshair.svg";
import { useLoanApply } from "./Component/LoanApplyContext";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const ApplyLink = ({ loanType }) => {
  const { openApply } = useLoanApply();

  return (
    <button
      type="button"
      onClick={() => openApply({ loanType })}
      style={{
        ...raleway,
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontWeight: 700,
        fontSize: "16px",
        lineHeight: "16px",
        color: "#86C7A8",
        textDecoration: "none",
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
    >
      Apply <MdChevronRight size={16} />
    </button>
  );
};

const CardContent = ({ title, description, loanType }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <h3
        style={{
          ...raleway,
          fontWeight: 600,
          fontSize: "20px",
          lineHeight: "30px",
          color: "#FFFFFF",
          margin: 0,
        }}
      >
        {title}
      </h3>
      <p
        style={{
          ...raleway,
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "24px",
          color: "#DEDEDE",
          margin: 0,
        }}
      >
        {description}
      </p>
    </div>
    <ApplyLink loanType={loanType} />
  </div>
);

const LoanOptions = () => (
  <>
    <style>{`
			@media (max-width: 900px) {
				.loan-opts-grid { grid-template-columns: 1fr !important; }
				.loan-opts-left { min-height: 400px !important; }
				.loan-opts-right-card { height: auto !important; min-height: 220px; }
			}
			@media (max-width: 600px) {
				.loan-opts-right-col { flex-direction: column !important; }
			}
			@media (max-width: 767px) {
				.loan-opt-content {
					max-width: 100% !important;
					padding: 28px 24px 120px !important;
				}
				.loan-opt-illus { width: 96px !important; right: 6% !important; bottom: 24px !important; }
				.loan-opts-left { min-height: unset !important; }
				.loan-opts-right-card { min-height: unset !important; }
			}
		`}</style>

    <section
      style={{
        background: "#132A22",
        position: "relative",
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        boxSizing: "border-box",
        padding: "clamp(48px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
      }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", width: "100%" }}>
        {/* Label */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "clamp(12px, 1.39vw, 20px)",
          }}
        >
          <span
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#86C7A8",
              flexShrink: 0,
              display: "inline-block",
            }}
          />
          <span
            style={{
              ...geistMono,
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "1",
              letterSpacing: "0.1em",
              color: "#86C7A8",
              textTransform: "uppercase",
            }}
          >
            Loan Options
          </span>
        </div>

        <h2
          style={{
            ...instrumentSerif,
            fontWeight: 400,
            fontSize: "clamp(28px, 4.17vw, 60px)",
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
            color: "#FFFFFF",
            margin: "0 0 clamp(24px, 2.78vw, 40px) 0",
            maxWidth: "min(900px, 100%)",
          }}
        >
          Loan options designed around your goals.
        </h2>

        {/* Cards grid */}
        <div
          className="loan-opts-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "614fr 644fr",
            gap: "22px",
            maxWidth: "1280px",
            width: "100%",
          }}
        >
          {/* Left — Business Loans */}
          <div
            className="loan-opts-left"
            style={{
              position: "relative",
              borderRadius: "8px",
              overflow: "hidden",
              minHeight: "588px",
              background: "#182E26",
              border: "1px solid #364A43",
            }}
          >
            <img
              className="loan-opt-illus"
              src={businessIllustration}
              alt=""
              aria-hidden="true"
              style={{
                position: "absolute",
                right: "5%",
                bottom: "6%",
                width: "58%",
                height: "auto",
                pointerEvents: "none",
              }}
            />
            <div
              className="loan-opt-content"
              style={{
                position: "relative",
                zIndex: 1,
                padding: "40px",
                maxWidth: "min(360px, 100%)",
              }}
            >
              <CardContent
                title="Business Loans"
                description="Get extra funds to buy stock, pay suppliers, expand your shop, or keep your business running smoothly."
                loanType="Business Loans"
              />
            </div>
          </div>

          {/* Right column — stacked cards */}
          <div
            className="loan-opts-right-col"
            style={{ display: "flex", flexDirection: "column", gap: "22px" }}
          >
            {/* Personal Loans */}
            <div
              className="loan-opts-right-card"
              style={{
                flex: 1,
                position: "relative",
                borderRadius: "8px",
                overflow: "hidden",
                height: "283px",
                background: "#182E26",
                border: "1px solid #364A43",
              }}
            >
              <img
                className="loan-opt-illus"
                src={personalIllustration}
                alt=""
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: "5%",
                  bottom: "10%",
                  width: "30%",
                  height: "auto",
                  pointerEvents: "none",
                }}
              />
              <div
                className="loan-opt-content"
                style={{
                  position: "relative",
                  zIndex: 1,
                  padding: "40px",
                  maxWidth: "min(60%, 380px)",
                }}
              >
                <CardContent
                  title="Personal Loans"
                  description="Handle school fees, rent, medical bills, travel plans, or other important expenses with flexible repayment options."
                  loanType="Personal Loans"
                />
              </div>
            </div>

            {/* Z-ForHer Credit Line */}
            <div
              className="loan-opts-right-card"
              style={{
                flex: 1,
                position: "relative",
                borderRadius: "8px",
                overflow: "hidden",
                height: "283px",
                background: "#182E26",
                border: "1px solid #364A43",
              }}
            >
              <img
                className="loan-opt-illus"
                src={zForHerIllustration}
                alt=""
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: "5%",
                  bottom: "8%",
                  width: "34%",
                  height: "auto",
                  pointerEvents: "none",
                }}
              />
              <div
                className="loan-opt-content"
                style={{
                  position: "relative",
                  zIndex: 1,
                  padding: "40px",
                  maxWidth: "min(60%, 380px)",
                }}
              >
                <CardContent
                  title="Z-ForHer Credit Line"
                  description="Funding created to help women start, run, or grow their businesses with confidence."
                  loanType="Z-ForHer Credit Line"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default LoanOptions;
